import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Button,
  TextField,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SelectField from '../inputs/SelectField';

const EMPTY_FORM = {
  sku: '', shape: '', length: '', width: '', totalCarat: '', pricePerCarat: '', caratPerStone: '',
};

/**
 * Add lot dialog with local form state so typing does not re-render the inventory page.
 */
export default function AddLotDialog({
  open,
  onClose,
  onSaved,
  shapeOptions = [],
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError('');
  }, [open]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const shapeSelectOptions = useMemo(
    () => shapeOptions.map((s) => {
      const shape = String(s).toUpperCase();
      return { value: shape, label: shape };
    }),
    [shapeOptions],
  );

  const handleAdd = async () => {
    if (loading) return;
    const totalCarat = parseFloat(form.totalCarat);
    const pricePerCarat = parseFloat(form.pricePerCarat);
    const isRound = form.shape === 'ROUND';
    let length = parseFloat(form.length);
    let width = parseFloat(form.width);
    if (isRound && form.width !== '' && !isNaN(width) && width >= 0) length = width;
    if (isRound && form.length !== '' && !isNaN(length) && length >= 0) width = length;
    if (!form.shape || (isRound ? (form.width === '' && form.length === '') : (form.length === '' || form.width === '')) || isNaN(length) || length < 0 || isNaN(width) || width < 0 || isNaN(totalCarat) || totalCarat <= 0 || isNaN(pricePerCarat) || pricePerCarat < 0) {
      setError(isRound ? 'Shape, width (diameter), total carat (positive), and price per carat are required' : 'Shape, length, width, total carat (positive), and price per carat are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/non-certified-diamonds', {
        shape: form.shape,
        length,
        width,
        totalCarat,
        pricePerCarat,
        caratPerStone: form.caratPerStone ? parseFloat(form.caratPerStone) : undefined,
        sku: form.sku?.trim() || undefined,
      });
      onClose();
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add non-certified diamond');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{t('inventory.addLot')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth label="SKU (optional)" value={form.sku} onChange={(e) => setField('sku', e.target.value)} sx={{ mb: 2 }} size="small" />
        <SelectField
          label="Shape"
          fullWidth
          sx={{ mb: 2 }}
          size="small"
          value={form.shape}
          onChange={(next) => setField('shape', next)}
          options={shapeSelectOptions}
          emptyLabel="Select..."
          includeEmptyOption
        />
        {form.shape === 'ROUND' ? (
          <TextField
            fullWidth
            label="Width / diameter (mm)"
            type="number"
            value={form.width}
            onChange={(e) => {
              const next = e.target.value;
              setForm((prev) => ({ ...prev, width: next, length: next }));
            }}
            inputProps={{ step: 0.1, min: 0 }}
            sx={{ mb: 2 }}
            size="small"
          />
        ) : (
          <>
            <TextField fullWidth label="Length (mm)" type="number" value={form.length} onChange={(e) => setField('length', e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ mb: 2 }} size="small" />
            <TextField fullWidth label="Width (mm)" type="number" value={form.width} onChange={(e) => setField('width', e.target.value)} inputProps={{ step: 0.1, min: 0 }} sx={{ mb: 2 }} size="small" />
          </>
        )}
        <TextField fullWidth label="Total carat" type="number" value={form.totalCarat} onChange={(e) => setField('totalCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} sx={{ mb: 2 }} size="small" />
        <TextField fullWidth label="Price per ct" type="number" value={form.pricePerCarat} onChange={(e) => setField('pricePerCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} sx={{ mb: 2 }} size="small" />
        <TextField fullWidth label="Ct per stone (optional)" type="number" value={form.caratPerStone} onChange={(e) => setField('caratPerStone', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('inventory.cancel')}</Button>
        <Button onClick={handleAdd} variant="contained" disabled={loading} startIcon={<AddIcon />}>{t('inventory.add')}</Button>
      </DialogActions>
    </Dialog>
  );
}
